import { inject, Injectable } from '@angular/core';
import { defer, from, map, switchMap, throwError } from 'rxjs';
import { COMMO_SHARED_SUPABASE_CLIENT } from '../commo-shared-data-access.config';
import {
  NOTICE_IMAGES_BUCKET,
  SIGNED_URL_BATCH_MAX_PATHS,
  SIGNED_URL_EXPIRES_SEC,
} from '../constants/notice-images';
import type { NoticeImage } from '../models/notice-image.model';
import { compressImageForUpload } from '../utils/compress-image-for-upload';

type SignedUrlCacheEntry = {
  url: string;
  /** この時刻を過ぎたら再取得する（expiresIn より少し早め）。 */
  expiresAtMs: number;
};

type SignedUrlWaiter = {
  resolve: (url: string) => void;
  reject: (error: Error) => void;
};

type SignNoticeImagesResponse = {
  signed?: Array<{ path: string; signedUrl: string }>;
  denied?: Array<{ path: string; error: string }>;
  expiresIn?: number;
  error?: string;
};

/** 期限の 2 分前にキャッシュを切る（時計ずれ・遅延耐性）。 */
const SIGNED_URL_CACHE_SKEW_MS = 2 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class NoticeImagesService {
  private readonly supabase = inject(COMMO_SHARED_SUPABASE_CLIENT);
  private readonly signedUrlCache = new Map<string, SignedUrlCacheEntry>();
  private readonly batchWaiters = new Map<string, SignedUrlWaiter[]>();
  private batchScheduled = false;
  private batchExpiresIn = SIGNED_URL_EXPIRES_SEC;

  /**
   * Signed URL for `<img src>`。同一 tick の複数要求は Edge `sign-notice-images`
   * にまとめ（一括 ACL）、有効期限内はメモリキャッシュを返す。
   */
  createSignedUrl(storagePath: string, expiresIn = SIGNED_URL_EXPIRES_SEC) {
    return defer(() => from(this.resolveSignedUrl(storagePath, expiresIn)));
  }

  /**
   * Compress → register row → upload. On upload failure, deletes the registered row.
   * Caller should reload the notice after success (no Apollo refetch here).
   */
  uploadImage(noticeId: string, file: File) {
    return from(compressImageForUpload(file)).pipe(
      switchMap((blob) =>
        from(
          this.supabase.rpc('register_notice_image', {
            p_notice_id: noticeId,
          }),
        ).pipe(
          switchMap(({ data, error }) => {
            if (error) {
              return throwError(() => new Error(error.message));
            }
            const row = normalizeRegisterRow(data);
            if (!row) {
              return throwError(
                () => new Error('Failed to register notice image.'),
              );
            }
            return from(
              this.supabase.storage
                .from(NOTICE_IMAGES_BUCKET)
                .upload(row.storage_path, blob, {
                  contentType: 'image/jpeg',
                  upsert: false,
                }),
            ).pipe(
              switchMap(({ error: uploadError }) => {
                if (uploadError) {
                  return from(
                    this.supabase.rpc('delete_notice_image', {
                      p_id: row.id,
                    }),
                  ).pipe(
                    switchMap(({ error: rollbackError }) => {
                      if (rollbackError) {
                        return throwError(
                          () =>
                            new Error(
                              `${uploadError.message} (rollback failed: ${rollbackError.message})`,
                            ),
                        );
                      }
                      return throwError(() => new Error(uploadError.message));
                    }),
                  );
                }
                return from([row]);
              }),
            );
          }),
        ),
      ),
    );
  }

  /**
   * RPC でメタデータ削除 → Storage API で実ファイル削除。
   * 公開中の最終1枚も削除可（ファイルを先に消さない）。
   * Storage 掃除失敗はメタ削除後なので成功扱い（孤児ファイルのみ）。
   */
  deleteImage(imageId: string, storagePath: string) {
    return from(
      this.supabase.rpc('delete_notice_image', { p_id: imageId }),
    ).pipe(
      switchMap(({ error }) => {
        if (error) {
          return throwError(() => new Error(error.message));
        }
        this.invalidateSignedUrl(storagePath);
        return from(
          this.supabase.storage
            .from(NOTICE_IMAGES_BUCKET)
            .remove([storagePath]),
        ).pipe(
          map(({ error: removeError }) => {
            if (removeError) {
              // メタは消えている。孤児ファイルは許容。
            }
          }),
        );
      }),
    );
  }

  /** お知らせ削除前に Storage 上のファイルをまとめて消す。 */
  removeStoragePaths(paths: string[]) {
    const unique = [...new Set(paths.filter((p) => !!p))];
    if (unique.length === 0) {
      return from([undefined]);
    }
    for (const path of unique) {
      this.invalidateSignedUrl(path);
    }
    return from(
      this.supabase.storage.from(NOTICE_IMAGES_BUCKET).remove(unique),
    ).pipe(
      map(({ error }) => {
        if (error) {
          throw new Error(error.message);
        }
      }),
    );
  }

  private invalidateSignedUrl(storagePath: string): void {
    this.signedUrlCache.delete(storagePath);
  }

  private resolveSignedUrl(
    storagePath: string,
    expiresIn: number,
  ): Promise<string> {
    const cached = this.signedUrlCache.get(storagePath);
    if (cached && cached.expiresAtMs > Date.now()) {
      return Promise.resolve(cached.url);
    }

    return new Promise<string>((resolve, reject) => {
      const waiters = this.batchWaiters.get(storagePath) ?? [];
      waiters.push({ resolve, reject });
      this.batchWaiters.set(storagePath, waiters);
      this.batchExpiresIn = Math.max(this.batchExpiresIn, expiresIn);
      if (!this.batchScheduled) {
        this.batchScheduled = true;
        queueMicrotask(() => {
          void this.flushSignedUrlBatch();
        });
      }
    });
  }

  private async flushSignedUrlBatch(): Promise<void> {
    const waitersByPath = new Map(this.batchWaiters);
    const expiresIn = this.batchExpiresIn;
    this.batchWaiters.clear();
    this.batchScheduled = false;
    this.batchExpiresIn = SIGNED_URL_EXPIRES_SEC;

    const paths = [...waitersByPath.keys()];
    if (paths.length === 0) {
      return;
    }

    const failAll = (error: Error) => {
      for (const waiters of waitersByPath.values()) {
        for (const w of waiters) {
          w.reject(error);
        }
      }
    };

    try {
      // クラウドは Edge で一括 ACL＋service role 署名（Storage RLS の行ごと notices 参照を避ける）。
      // ローカル等で Edge が無い／落ちているときは Storage createSignedUrls にフォールバック。
      const signed: Array<{ path: string; signedUrl: string }> = [];
      const denied: Array<{ path: string; error: string }> = [];
      let ttlSec = expiresIn;
      let usedEdge = false;

      for (let i = 0; i < paths.length; i += SIGNED_URL_BATCH_MAX_PATHS) {
        const chunk = paths.slice(i, i + SIGNED_URL_BATCH_MAX_PATHS);
        const { data, error } =
          await this.supabase.functions.invoke<SignNoticeImagesResponse>(
            'sign-notice-images',
            { body: { paths: chunk, expiresIn } },
          );

        if (error || !data || data.error) {
          usedEdge = false;
          break;
        }
        usedEdge = true;
        signed.push(...(data.signed ?? []));
        denied.push(...(data.denied ?? []));
        if (typeof data.expiresIn === 'number') {
          ttlSec = data.expiresIn;
        }
      }

      if (!usedEdge) {
        signed.length = 0;
        denied.length = 0;
        const { data, error } = await this.supabase.storage
          .from(NOTICE_IMAGES_BUCKET)
          .createSignedUrls(paths, expiresIn);
        if (error || !data) {
          failAll(new Error(error?.message ?? 'Failed to create image URLs.'));
          return;
        }
        for (const row of data) {
          const path = row.path ?? '';
          if (row.signedUrl && path && !row.error) {
            signed.push({ path, signedUrl: row.signedUrl });
          } else if (path) {
            denied.push({
              path,
              error: row.error ?? 'Failed to create image URL.',
            });
          }
        }
      }

      const expiresAtMs =
        Date.now() + Math.max(ttlSec * 1000 - SIGNED_URL_CACHE_SKEW_MS, 0);
      const byPath = new Map(
        signed.map((row) => [row.path, row.signedUrl] as const),
      );
      const deniedByPath = new Map(
        denied.map((row) => [row.path, row.error] as const),
      );

      for (const path of paths) {
        const waiters = waitersByPath.get(path) ?? [];
        const url = byPath.get(path);
        if (!url) {
          const err = new Error(
            deniedByPath.get(path) ?? 'Failed to create image URL.',
          );
          for (const w of waiters) {
            w.reject(err);
          }
          continue;
        }
        this.signedUrlCache.set(path, { url, expiresAtMs });
        for (const w of waiters) {
          w.resolve(url);
        }
      }
    } catch (e) {
      failAll(
        e instanceof Error ? e : new Error('Failed to create image URLs.'),
      );
    }
  }
}

function normalizeRegisterRow(data: unknown): NoticeImage | null {
  const row = Array.isArray(data) ? data[0] : data;
  if (
    row &&
    typeof row === 'object' &&
    'id' in row &&
    'storage_path' in row &&
    'sort_order' in row &&
    typeof (row as NoticeImage).id === 'string' &&
    typeof (row as NoticeImage).storage_path === 'string' &&
    typeof (row as NoticeImage).sort_order === 'number'
  ) {
    return row as NoticeImage;
  }
  return null;
}
